import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { RefreshCw } from "lucide-react";

import IdentityVerificationIcon from "../../assets/images/icons/70d011a15fd0a3b49eaaaf0c6ce30f98fa317e63.png";
import Step1Icon from "../../assets/images/icons/ec8a547022d7bc2df94d81a7ccc6bc6cab485a3a.png";
import Step2Icon from "../../assets/images/icons/8a3ae6bc017e09cebe7cd44af5dd91c340e58db9.png";
import Step3Icon from "../../assets/images/icons/9047e3ae413d79ebc9cbfa6a8089d3646d03d565.png";
import SuccessfulVerificationIcon from "../../assets/images/icons/f9d1e315c3bea4aa050b61ff98799f2116516fc8.png";
import UnsuccessfulVerificationIcon from "../../assets/images/icons/e4d5d1416eedab24ec8763a525fb31332b5e2e99.png";
import PendingVerificationIcon from "../../assets/images/icons/4136ac5e2a265e7b43b9329d8aedfd2417390380.png";

import chooseUserTypeStyle from "../styles/chooseUserTypeStyle";
import complatedProfileStyle from "../styles/complateProfileStyle";

interface CreatorVerificationStepProps {
  isMobile: boolean;
  diditVerificationData: any;
  sessionUrl: string | undefined;
  popupBlocked: boolean;
  setPopupBlocked: (blocked: boolean) => void;
  startDiditVerification: () => void;
  handleManualPopupOpen: () => void;
  prevStep: () => void;
  nextStep: () => void;
  agreementAccepted: boolean;
  setAgreementAccepted: (accepted: boolean) => void;
  imageUsageAgreed: boolean;
  setImageUsageAgreed: (agreed: boolean) => void;
  isFinalizing: boolean;
  handleCompleteOnboarding: () => Promise<void>;
  stagingCreatorBypass: boolean;
  getStatusMessage: (status: string) => { title: string; message: string; color: string };
}

const CreatorVerificationStep: React.FC<CreatorVerificationStepProps> = ({
  isMobile,
  diditVerificationData,
  sessionUrl,
  popupBlocked,
  setPopupBlocked,
  startDiditVerification,
  handleManualPopupOpen,
  prevStep,
  nextStep,
  agreementAccepted,
  setAgreementAccepted,
  imageUsageAgreed,
  setImageUsageAgreed,
  isFinalizing,
  handleCompleteOnboarding,
  stagingCreatorBypass,
  getStatusMessage,
}) => {
  const profileStyle = complatedProfileStyle();
  const creatorClasses = chooseUserTypeStyle({ selected: true });

  const verificationStatus =
    diditVerificationData?.didit_verifications?.[0]?.status || "Not Started";
  const isVerificationStarted =
    sessionUrl ||
    verificationStatus === "In Progress" ||
    verificationStatus === "Pending";
  const isVerificationApproved = verificationStatus === "Approved";
  const isVerificationPending =
    (verificationStatus === "Pending" || verificationStatus === "In Progress") &&
    !sessionUrl;
  const isVerificationUnsuccessful =
    verificationStatus !== "Approved" &&
    verificationStatus !== "Not Started" &&
    verificationStatus !== "In Progress" &&
    verificationStatus !== "Pending";

  // If verification unsuccessful, show failure screen
  if (isVerificationUnsuccessful) {
    return (
      <Paper className={profileStyle.formCard} style={{ maxWidth: 838 }}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Unsuccessful Icon */}
          <img
            src={UnsuccessfulVerificationIcon}
            alt="Verification Unsuccessful"
            style={{
              width: isMobile ? "120px" : "200px",
              height: "auto",
              marginBottom: isMobile ? "16px" : "20px",
              transform: "scale(0.8)",
            }}
          />

          {/* Title */}
          <Typography
            style={{
              fontSize: isMobile ? "20px" : "32px",
              fontWeight: 600,
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: isMobile ? "16px" : "20px",
              width: "100%",
            }}
          >
            Verification unsuccessful
          </Typography>

          {/* Subtitle */}
          <Typography
            style={{
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: 400,
              color: "#FFFFFFCC",
              textAlign: "center",
              marginBottom: "32px",
              maxWidth: "600px",
              width: "100%",
              lineHeight: 1.5,
            }}
          >
            We couldn't complete your verification this time. Don't worry — we'll
            review it again, and if your documents meet the requirements, you'll
            receive an email notification soon.
          </Typography>

          {/* Try Again Button */}
          <Button
            variant="outlined"
            className={creatorClasses.button}
            onClick={startDiditVerification}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #FE5A97",
              color: "#fff",
              width: "100%",
              maxWidth: "400px",
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: isMobile ? 500 : 600,
            }}
          >
            Try verification again
          </Button>
        </Box>
      </Paper>
    );
  }

  // If verification approved, show success screen
  if (isVerificationApproved) {
    return (
      <Paper className={profileStyle.formCard} style={{ maxWidth: 838 }}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Success Icon */}
          <img
            src={SuccessfulVerificationIcon}
            alt="Successful Verification"
            style={{
              width: isMobile ? "120px" : "200px",
              height: "auto",
              marginBottom: isMobile ? "20px" : "25px",
              transform: "scale(0.8)",
            }}
          />

          {/* Title */}
          <Typography
            style={{
              fontSize: isMobile ? "20px" : "32px",
              fontWeight: 600,
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: isMobile ? "16px" : "20px",
              width: "100%",
            }}
          >
            Successful Verification
          </Typography>

          {/* Subtitle */}
          <Typography
            style={{
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: 400,
              color: "#FFFFFFCC",
              textAlign: "center",
              marginBottom: "32px",
              maxWidth: "768px",
              width: "100%",
            }}
          >
            One last step before we continue — please review and agree to our
            rules.
          </Typography>

          {/* Checkboxes */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              width: "100%",
              marginBottom: "32px",
              maxWidth: 500,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreementAccepted}
                  onChange={e => setAgreementAccepted(e.target.checked)}
                  style={{
                    color: "#FFFFFF",
                  }}
                />
              }
              label={
                <Typography
                  style={{
                    color: "#FFFFFF",
                    fontSize: isMobile ? "12px" : "20px",
                    fontWeight: 400,
                  }}
                >
                  I agree to the{" "}
                  <a
                    href="/content-provider-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    Content Provider Agreement
                  </a>
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={imageUsageAgreed}
                  onChange={e => setImageUsageAgreed(e.target.checked)}
                  style={{
                    color: "#FFFFFF",
                  }}
                />
              }
              label={
                <Typography
                  style={{
                    color: "#FFFFFF",
                    fontSize: isMobile ? "12px" : "20px",
                    fontWeight: 400,
                    whiteSpace: "pre-line",
                  }}
                >
                  I understand and agree that my images may{"\n"}
                  be used and publicly displayed on the website.
                </Typography>
              }
            />
          </Box>

          {/* Complete Onboarding Button */}
          <Button
            variant="contained"
            className={creatorClasses.button}
            disabled={!agreementAccepted || !imageUsageAgreed || isFinalizing}
            onClick={handleCompleteOnboarding}
            style={{
              width: "100%",
              maxWidth: "400px",
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: isMobile ? 500 : 600,
            }}
          >
            {isFinalizing ? "Completing..." : "Start your Sugar Cribs journey"}
          </Button>
        </Box>
      </Paper>
    );
  }

  // If verification pending (no active session), show pending screen
  if (isVerificationPending) {
    return (
      <Paper className={profileStyle.formCard} style={{ maxWidth: 838 }}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Pending Icon */}
          <img
            src={PendingVerificationIcon}
            alt="Pending Verification"
            style={{
              width: isMobile ? "120px" : "200px",
              height: "auto",
              marginBottom: isMobile ? "16px" : "20px",
              transform: "scale(0.8)",
            }}
          />

          {/* Title */}
          <Typography
            style={{
              fontSize: isMobile ? "20px" : "32px",
              fontWeight: 600,
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: isMobile ? "16px" : "20px",
              width: "100%",
            }}
          >
            Pending Verification
          </Typography>

          {/* Subtitle */}
          <Typography
            style={{
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: 400,
              color: "#FFFFFFCC",
              textAlign: "center",
              maxWidth: "600px",
              width: "100%",
              lineHeight: 1.5,
            }}
          >
            We're taking a bit more time to review your ID.
          </Typography>

          <Typography
            style={{
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: 400,
              color: "#FFFFFFCC",
              textAlign: "center",
              maxWidth: "600px",
              marginBottom: "12px",
              width: "100%",
              lineHeight: 1.5,
            }}
          >
            You'll get an email as soon as it's done.
          </Typography>

          <Typography
            style={{
              fontSize: isMobile ? "14px" : "20px",
              fontWeight: 400,
              color: "#FFFFFFCC",
              textAlign: "center",
              marginBottom: "32px",
              maxWidth: "600px",
              width: "100%",
              lineHeight: 1.5,
            }}
          >
            No response in 48 hours? Drop us an email!
            <br />
            <a
              href="mailto:support@sugarcribs.com"
              style={{
                color: "#FF78AA",
                textDecoration: "underline",
              }}
            >
              support@sugarcribs.com
            </a>
          </Typography>
        </Box>
      </Paper>
    );
  }

  // If verification started, show only iframe and buttons
  if (isVerificationStarted) {
    return (
      <Paper className={profileStyle.formCard} style={{ maxWidth: 838 }}>
        {/* Show iframe for in_progress status */}
        {sessionUrl && (
          <Box
            style={{
              margin: "20px 0",
              border: "2px solid #6941C6",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Box
              style={{
                backgroundColor: "#6941C6",
                color: "white",
                padding: "12px 16px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              🔐 Identity Verification
            </Box>
            <iframe
              src={sessionUrl}
              width="100%"
              height="600"
              frameBorder="0"
              style={{
                display: "block",
                border: "none",
              }}
              title="Didit Identity Verification"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
            <Box
              style={{
                backgroundColor: "#f8f9fa",
                padding: "12px 16px",
                fontSize: "12px",
                color: "#666",
                borderTop: "1px solid #e9ecef",
              }}
            >
              💡 Complete the verification steps above. Once finished, you'll be
              able to proceed to the next step and start your creator journey!
            </Box>
          </Box>
        )}

        {/* Popup blocked message */}
        {popupBlocked && (
          <Box
            style={{
              backgroundColor: "#FFE4E1",
              border: "1px solid #FF6B6B",
              borderRadius: "8px",
              padding: "16px",
              margin: "20px 0",
              textAlign: "left",
            }}
          >
            <Typography
              style={{
                color: "#D32F2F",
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              🚫 Popup Blocked
            </Typography>
            <Typography
              style={{
                color: "#D32F2F",
                fontSize: "12px",
                marginBottom: "12px",
              }}
            >
              Your browser blocked the verification popup. Please:
            </Typography>
            <Box style={{ marginLeft: "16px" }}>
              <Typography
                style={{
                  color: "#D32F2F",
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                1. Click the popup blocker icon in your browser's address bar
              </Typography>
              <Typography
                style={{
                  color: "#D32F2F",
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                2. Select "Always allow popups from this site"
              </Typography>
              <Typography
                style={{
                  color: "#D32F2F",
                  fontSize: "12px",
                  marginBottom: "12px",
                }}
              >
                3. Click "Restart Verification" again
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleManualPopupOpen}
              style={{
                backgroundColor: "#D32F2F",
                color: "white",
                fontSize: "12px",
                padding: "8px 16px",
                marginRight: "8px",
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => setPopupBlocked(false)}
              style={{
                borderColor: "#D32F2F",
                color: "#D32F2F",
                fontSize: "12px",
                padding: "8px 16px",
              }}
            >
              Dismiss
            </Button>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box
          marginTop={isMobile ? "32px" : "60px"}
          display="flex"
          justifyContent="space-between"
          width="100%"
        >
          <Button
            variant="outlined"
            className={creatorClasses.button}
            onClick={prevStep}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #FE5A97",
              color: "#FE5A97",
            }}
          >
            Back
          </Button>

          <Box className={creatorClasses.buttonContainer}>
            <Button
              variant="contained"
              className={creatorClasses.button}
              onClick={async () => {
                if (verificationStatus === "Approved") {
                  await handleCompleteOnboarding();
                } else {
                  startDiditVerification();
                }
              }}
              startIcon={<RefreshCw size={18} />}
            >
              {verificationStatus === "Approved" ? "Complete" : "Restart Verification"}
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  // If verification not started, show full content
  const statusInfo = getStatusMessage(verificationStatus);

  return (
    <Paper className={profileStyle.formCard} style={{ maxWidth: 838 }}>
      {/* New Identity Verification Content */}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "32px",
          width: "100%",
        }}
      >
        {/* Main Image */}
        <img
          src={IdentityVerificationIcon}
          alt="Identity Verification"
          style={{
            width: isMobile ? "120px" : "234px",
            height: "auto",
            marginBottom: "24px",
            alignSelf: "center",
          }}
        />

        {/* Title */}
        <Typography
          style={{
            fontSize: isMobile ? "20px" : "32px",
            fontWeight: isMobile ? 700 : 600,
            color: "#FFFFFF",
            textAlign: "center",
            marginBottom: "16px",
            width: "100%",
          }}
        >
          Complete identity verification
        </Typography>

        {/* Subtitle */}
        <Typography
          style={{
            fontSize: isMobile ? "12px" : "20px",
            fontWeight: 400,
            color: isMobile ? "#FFFFFF" : "#FFFFFFCC",
            textAlign: "center",
            marginBottom: "32px",
            maxWidth: "600px",
            width: "100%",
          }}
        >
          We need to quickly verify your identity to keep the platform safe for
          everyone.
        </Typography>
        {isMobile && (
          <Typography
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: "32px",
              maxWidth: "600px",
              width: "100%",
            }}
          >
            Verification is handled through a secure service so your data stays
            private and is used only for identification purposes.
          </Typography>
        )}

        {/* Navigation Buttons for Mobile - Show after subtitle */}
        {isMobile && (
          <Box marginBottom="32px" display="flex" justifyContent="space-between" width="100%">
            <Button
              variant="outlined"
              className={creatorClasses.button}
              onClick={prevStep}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #FE5A97",
                color: "#FE5A97",
              }}
            >
              Back
            </Button>

            <Box className={creatorClasses.buttonContainer}>
              <Button
                variant="contained"
                className={creatorClasses.button}
                onClick={() => {
                  if (verificationStatus === "Approved") {
                    nextStep();
                  } else {
                    startDiditVerification();
                  }
                }}
              >
                {verificationStatus === "Approved" ? "Next" : "Start verification"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Steps Blocks */}
        <Box
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "16px",
            width: "100%",
            marginBottom: "32px",
          }}
        >
          {/* Step 1 */}
          <Box
            style={{
              backgroundColor: "#1F1F1F",
              borderRadius: "12px",
              padding: "24px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <img
              src={Step1Icon}
              alt="Step 1"
              style={{
                width: "64px",
                height: "64px",
                marginBottom: "16px",
              }}
            />
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#FFFFFF",
                marginBottom: "8px",
              }}
            >
              Step 1
            </Typography>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1.5,
              }}
            >
              Take a clear photo of your ID following the on-screen instructions.
            </Typography>
          </Box>

          {/* Step 2 */}
          <Box
            style={{
              backgroundColor: "#1F1F1F",
              borderRadius: "12px",
              padding: "24px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <img
              src={Step2Icon}
              alt="Step 2"
              style={{
                width: "64px",
                height: "64px",
                marginBottom: "16px",
              }}
            />
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#FFFFFF",
                marginBottom: "8px",
              }}
            >
              Step 2
            </Typography>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1.5,
              }}
            >
              Take a quick selfie and complete the liveness check.
            </Typography>
          </Box>

          {/* Step 3 */}
          <Box
            style={{
              backgroundColor: "#1F1F1F",
              borderRadius: "12px",
              padding: "24px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <img
              src={Step3Icon}
              alt="Step 3"
              style={{
                width: "64px",
                height: "64px",
                marginBottom: "16px",
              }}
            />
            <Typography
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#FFFFFF",
                marginBottom: "8px",
              }}
            >
              Step 3
            </Typography>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1.5,
              }}
            >
              We'll review your details and show your verification result.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dynamic Status Display - Only show if verification not started */}
      <Box style={{ textAlign: "center", margin: "20px 0" }}>
        {statusInfo.title && (
          <Typography
            style={{
              color: statusInfo.color,
              fontSize: "16px",
              marginBottom: "10px",
              fontWeight: "bold",
            }}
          >
            {statusInfo.title}
          </Typography>
        )}
        {statusInfo.message && (
          <Typography
            style={{
              color: "#FFFFFF",
              fontSize: "14px",
              marginBottom: "20px",
              lineHeight: "1.5",
            }}
          >
            {statusInfo.message}
          </Typography>
        )}
      </Box>

      {/* Navigation Buttons - Desktop only (mobile buttons are above steps) */}
      {!isMobile && (
        <Box marginTop="60px" display="flex" justifyContent="space-between" width="100%">
          <Button
            variant="outlined"
            className={creatorClasses.button}
            onClick={prevStep}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #FE5A97",
              color: "#FE5A97",
            }}
          >
            Back
          </Button>

          <Box className={creatorClasses.buttonContainer}>
            <Button
              variant="contained"
              className={creatorClasses.button}
              onClick={() => {
                if (verificationStatus === "Approved") {
                  nextStep();
                } else {
                  startDiditVerification();
                }
              }}
            >
              {verificationStatus === "Approved" ? "Next" : "Start verification"}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CreatorVerificationStep;
